import styled, { css } from 'styled-components'
import { LookButton, buttonLook, iconButtonLook } from 'components/Buttons/look'
import { NumericFormat } from 'react-number-format'

export const VALUE_COLUMN_WIDTH = 108
export const LABEL_COLUMN_WIDTH = 190

export const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  padding: 15px 0px 30px;
  gap: 16px;
`

export const FilterContainer = styled.div`
  display: flex;
  gap: 10px;
  align-self: center;
  width: 220px;
`

export const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding-top: 40px;
  gap: 8px;
`

export const InfoText = styled.p`
  color: #000000;
  font-size: 16px;
  text-align: center;
  margin: 0px;
`

export const WarningText = styled(InfoText)`
  color: #FF3939;
`

/*
  A tabela ocupa a largura toda disponível, sem padding lateral — quanto mais meses couberem sem
  precisar rolar, melhor. O scroll horizontal só entra quando a tela é mais estreita que a tabela
  inteira.

  A altura é limitada de propósito, com rolagem vertical própria: é o que permite ao cabeçalho
  grudar no topo (`position: sticky` no `HeaderCell`) sem bug. Tentamos deixar só o `overflow-x`
  ligado e grudar o cabeçalho no topo da página — `overflow-x: auto` sozinho já basta para o
  navegador tratar este container como "scroll container" também no eixo vertical (vira `auto`
  por tabela da spec do CSS mesmo sem pedir), e nessa combinação com a barra de abas do Financeiro
  (que também é sticky, por cima) o Chrome desenha o cabeçalho solto, flutuando por cima do
  conteúdo em vez de grudado. Com altura e rolagem internas o `position: sticky` do cabeçalho
  passa a valer para este container, não para a página, e some o bug.

  `isolation: isolate` dá a este container um contexto de empilhamento próprio: o `z-index` do
  cabeçalho e da coluna de descrição (2 e 3) vale só entre as células da tabela. Sem isso eles
  disputam a camada com a barra de abas do Financeiro (sticky, `z-index: 3`) e, como vêm depois no
  DOM, aparecem por cima dela ao rolar a página.

  Sem padding em cima: o cabeçalho gruda abaixo do padding do container, e com 2px ali as linhas
  da tabela apareciam passando por trás dele, numa fresta. A linha de cima da tabela, que esse
  padding deixava o `outline` desenhar, vem do `box-shadow` do `HeaderCell`.
*/
export const TableWrapper = styled.div`
  width: 100%;
  max-height: 65vh;
  overflow: auto;
  padding: 0px 15px 2px;
  isolation: isolate;

  /*
    "framed": a moldura é do próprio contêiner, que não rola, e não há padding lateral. Com
    padding sobra uma fresta à esquerda da coluna fixa por onde o conteúdo aparece, e com
    "border-collapse" a borda e o "outline" da tabela rolam junto com ela. Sem as bordas da
    tabela, a divisória da coluna fixa e a linha de baixo viram "box-shadow" das células, que
    acompanham a célula grudada.
  */
  ${({ framed }) => framed && css`
    padding: 0;
    border: 1px solid #CCCCCC;
    border-radius: 10px;

    table {
      outline: none;
    }

    th {
      box-shadow: none;
    }

    th:first-child {
      box-shadow: inset -1px 0 0 #CCCCCC, inset 0 -1px 0 #CCCCCC;
    }

    td:first-child {
      border-right: none;
      box-shadow: inset -1px 0 0 #CCCCCC, inset 0 -1px 0 #EEEEEE;
    }
  `}
`

/*
  `overflow: clip`, e não `hidden`: os dois recortam os cantos arredondados, mas o `hidden` faz da
  tabela um contêiner de rolagem. Aí o `position: sticky` do cabeçalho e da coluna de descrição
  passa a valer para a tabela, que não rola, e os dois deixam de grudar quando o `TableWrapper`
  rola.
*/
export const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  outline: 1px solid #CCCCCC;
  border-radius: 10px;
  table-layout: fixed;
  overflow: clip;
`

// O cabeçalho inteiro gruda ao rolar a tabela (topo de `TableWrapper`, que é quem rola — ver o
// comentário lá), e a coluna de descrição também gruda na borda esquerda (sticky nos dois eixos)
// — nunca perde de vista nem qual coluna nem qual linha está sendo lida.
export const HeaderCell = styled.th`
  background-color: #F0F0F0;
  border-bottom: 1px solid #CCCCCC;
  border-right: 1px solid #EEEEEE;
  box-shadow: inset 0px 1px 0px #CCCCCC;
  font-size: 13px;
  font-weight: 600;
  color: #252525;
  text-align: ${({ align }) => align === 'left' ? 'left' : 'center'};
  padding: 9px 10px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  position: sticky;
  top: 0px;
  left: ${({ sticky }) => sticky ? '0px' : 'auto'};
  z-index: ${({ sticky }) => sticky ? 3 : 2};
`

export const LabelCell = styled.td`
  background-color: ${({ kind }) => kind === 'total' ? '#F0F0F0' : '#FFFFFF'};
  border-bottom: 1px solid #EEEEEE;
  border-right: 1px solid #CCCCCC;
  padding: 9px 10px 9px ${({ kind }) => kind === 'child' ? '26' : '10'}px;
  font-size: ${({ kind }) => kind === 'child' ? '12px' : '13px'};
  font-weight: ${({ kind }) => kind === 'total' ? '600' : '400'};
  color: ${({ kind }) => kind === 'child' ? '#666666' : '#252525'};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  position: sticky;
  left: 0px;
  z-index: 1;
`

export const ValueCell = styled.td`
  background-color: ${({ kind }) => kind === 'total' ? '#F0F0F0' : '#FFFFFF'};
  border-bottom: 1px solid #EEEEEE;
  border-right: 1px solid #EEEEEE;
  padding: 9px 10px;
  text-align: right;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`

export const MoneyText = styled(NumericFormat).attrs(({ value }) => ({
  value: value.toFixed(2).replace('.', ','),
  thousandSeparator: '.',
  decimalSeparator: ',',
  displayType: 'text',
  prefix: 'R$ '
}))`
  color: ${({ value, kind }) => (
    kind === 'total' ? '#252525' : (parseFloat(value.replace(',', '.')) >= 0 ? '#44CF6C' : '#FF3939')
  )};
  font-size: 13px;
  font-weight: ${({ kind }) => kind === 'total' ? '600' : '400'};
`

export const PercentageText = styled.span`
  font-size: 13px;
  color: ${({ children }) => (children ?? '').toString().startsWith('-') ? '#FF3939' : '#252525'};
`

// ---- Gráficos ----

export const ChartContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  padding: 2px 15px;
  gap: 16px;
`

export const Section = styled.div`
  width: 100%;
  border: 1px solid #CCCCCC;
  border-radius: 10px;
  padding: 14px;
  display: flex;
  flex-direction: column;
  gap: 10px;
`

export const SectionTitle = styled.h3`
  color: #000000;
  font-size: 17px;
  font-weight: 600;
  margin: 0px;
`

export const EmptyText = styled(InfoText)`
  color: #777777;
  padding-top: 30px;
`

export const CardsRow = styled.div`
  display: flex;
  width: 100%;
  gap: 12px;
  flex-wrap: wrap;
`

export const Card = styled.div`
  flex: 1;
  min-width: 180px;
  border: 1px solid #CCCCCC;
  border-radius: 10px;
  padding: 12px 14px;
  display: flex;
  flex-direction: column;
  gap: 4px;
`

export const CardLabel = styled.span`
  color: #777777;
  font-size: 13px;
`

export const CardMoney = styled(NumericFormat).attrs(({ value }) => ({
  value: Number(value).toFixed(2).replace('.', ','),
  thousandSeparator: '.',
  decimalSeparator: ',',
  displayType: 'text',
  prefix: 'R$ '
}))`
  color: ${({ tone }) => tone ?? '#252525'};
  font-size: 20px;
  font-weight: 600;
`

export const CardHint = styled.span`
  color: #777777;
  font-size: 12px;
`

// ---- Despesas e configuração ----

export const ExpensesContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 760px;
  gap: 16px;
`

export const HintText = styled.p`
  color: #777777;
  font-size: 14px;
  margin: 0px;
`

export const ActionButton = styled(LookButton).attrs(buttonLook({ variant: 'secondary' }))`
  align-self: flex-start;
  min-width: 200px;
`

export const MonthTitle = styled.h4`
  color: #252525;
  font-size: 15px;
  font-weight: 600;
  text-transform: capitalize;
  margin: 10px 0px 4px;
`

export const ExpenseRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 0px;
  border-bottom: 1px solid #EEEEEE;
`

export const ExpenseInfo = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
`

export const ExpenseName = styled.span`
  color: #252525;
  font-size: 14px;
  font-weight: 600;
`

export const ExpenseMeta = styled.span`
  color: #777777;
  font-size: 12px;
`

export const IconButton = styled(LookButton).attrs(iconButtonLook())``
